import React, { useEffect, useState } from "react";

import Button from "../../../Button";
import Checkbox from "../../Checkbox";
import Input, { MaterialInputEvent } from "../../Input";
import Modal from "../../Modal";
import {
  AdminUsersApi,
  AdminRole,
  AdminUser,
  AdminUserErrors,
  AdminUserForm,
  userErrors,
} from "../../../../services/AdminUsersService";

import { useUserManagementTranslation, UserManagementResource } from "./useUserManagementTranslation";

import style from "./styles.module.scss";

const keyI18n = "translations.admin.users.form";
const MIN_PASSWORD = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PORTAL_ADMIN_ALL = "portal_admin:all";

interface Props {
  service: AdminUsersApi;
  resource: UserManagementResource;
  open: boolean;
  user: AdminUser | null;
  roles: AdminRole[];
  own: boolean;
  onClose(): void;
  onSaved(user: AdminUser, created: boolean): void;
}

export default function UserFormDialog({ service, resource, open, user, roles, own, onClose, onSaved }: Props) {
  const { t, i18n } = useUserManagementTranslation(resource);
  const editing = user !== null;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [changePassword, setChangePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<AdminUserErrors>({});
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setName(user && user.name ? user.name : "");
    setEmail(user ? user.email : "");
    setRoleIds(user ? user.roles.map((role) => role.id) : []);
    setChangePassword(!user);
    setPassword("");
    setPasswordConfirmation("");
    setShowPassword(false);
    setErrors({});
    setFailed(false);
    setSaving(false);
  }, [open, user]);

  if (!open) {
    return null;
  }

  const close = () => {
    if (!saving) {
      onClose();
    }
  };

  const errorText = (field: string) => {
    const codes = errors[field];
    if (!codes || codes.length === 0) {
      return undefined;
    }
    return t(`${keyI18n}.errors.${field}.${codes[0]}`, { defaultValue: t(`${keyI18n}.errors.invalid`), min: MIN_PASSWORD });
  };

  const validate = (): AdminUserErrors => {
    const found: AdminUserErrors = {};
    if (!name.trim()) found.name = ["blank"];
    if (!email.trim()) found.email = ["blank"];
    else if (!EMAIL_PATTERN.test(email.trim())) found.email = ["invalid"];
    if (roleIds.length === 0) found.roleIds = ["blank"];
    if (changePassword) {
      if (!password) found.password = ["blank"];
      else if (password.length < MIN_PASSWORD) found.password = ["too_short"];
      if (password && password !== passwordConfirmation) found.passwordConfirmation = ["confirmation"];
    }
    return found;
  };

  const submit = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (saving) {
      return;
    }

    const found = validate();
    setErrors(found);
    setFailed(false);
    if (Object.keys(found).length > 0) {
      return;
    }

    const form: AdminUserForm = { name: name.trim(), email: email.trim(), roleIds };
    if (changePassword) {
      form.password = password;
      form.passwordConfirmation = passwordConfirmation;
    }

    setSaving(true);
    const request = user ? service.update(user.id, form) : service.create(form);
    request
      .then((saved) => onSaved(saved, !user))
      .catch((error) => {
        const serverErrors = userErrors(error);
        if (serverErrors) {
          setErrors(serverErrors);
        } else {
          setFailed(true);
        }
        setSaving(false);
      });
  };

  const roleDescription = (role: AdminRole) => role.activities
    .map((activity) => `${keyI18n}.activityDescriptions.${activity.replace(/\W/g, "_")}`)
    .filter((key) => i18n.exists(key))
    .map((key) => t(key))
    .join(" ");

  const toggleRole = (id: number, checked: boolean) => {
    setRoleIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((value) => value !== id)));
  };

  const visibilityButton = (
    <Button
      iconButton
      variant="text"
      size="extra-small"
      type="button"
      aria-label={t(`${keyI18n}.${showPassword ? "hidePassword" : "showPassword"}`)}
      onClick={() => setShowPassword((value) => !value)}
      icon={<span className="material-symbols-sharp">{showPassword ? "visibility_off" : "visibility"}</span>}
    />
  );

  const rolesError = errorText("roleIds");
  const baseError = errors.base && errors.base.length > 0;

  return (
    <Modal
      open
      onClose={close}
      dismissible={!saving}
      icon={editing ? "manage_accounts" : "person_add"}
      title={t(`${keyI18n}.${editing ? "editTitle" : "createTitle"}`)}
      size="medium"
      actions={(
        <>
          <Button variant="text" size="small" onClick={close} disabled={saving}>
            {t(`${keyI18n}.cancel`)}
          </Button>
          <Button variant="filled" size="small" onClick={() => submit()} disabled={saving} softDisabled={saving}>
            {t(`${keyI18n}.${saving ? "saving" : "save"}`)}
          </Button>
        </>
      )}
    >
      <form className={style.form} noValidate onSubmit={submit}>
        <div className={style.field}>
          <Input
            variant="outlined"
            label={t(`${keyI18n}.name`)}
            value={name}
            required
            autoFocus
            autoComplete="off"
            fullWidth
            error={Boolean(errorText("name"))}
            errorText={errorText("name")}
            onInput={(event: MaterialInputEvent) => setName(event.currentTarget.value)}
          />
        </div>
        <div className={style.field}>
          <Input
            variant="outlined"
            type="email"
            label={t(`${keyI18n}.email`)}
            value={email}
            required
            autoComplete="off"
            fullWidth
            disabled={own}
            supportingText={own ? t(`${keyI18n}.ownEmail`) : undefined}
            error={Boolean(errorText("email"))}
            errorText={errorText("email")}
            onInput={(event: MaterialInputEvent) => setEmail(event.currentTarget.value)}
          />
        </div>

        <fieldset className={style.roles} aria-describedby={rolesError ? "user-roles-error" : undefined}>
          <legend className={`pgm-title-small ${style.legend}`}>{t(`${keyI18n}.roles`)}</legend>
          {roles.map((role) => {
            const lockOwnAccess = own && role.activities.includes(PORTAL_ADMIN_ALL) && roleIds.includes(role.id);
            const description = roleDescription(role);

            return (
              <div key={role.id} className={style.roleOption}>
                <Checkbox
                  id={`user-role-${role.id}`}
                  label={role.name}
                  checked={roleIds.includes(role.id)}
                  disabled={lockOwnAccess}
                  onChange={(checked) => toggleRole(role.id, checked)}
                />
                {(description || lockOwnAccess) && (
                  <span className={`pgm-body-small ${style.roleDescription}`}>
                    {lockOwnAccess ? t(`${keyI18n}.ownAccess`) : description}
                  </span>
                )}
              </div>
            );
          })}
          {rolesError && (
            <span className={`pgm-body-small ${style.fieldError}`} id="user-roles-error" role="alert">
              {rolesError}
            </span>
          )}
        </fieldset>

        {editing && !changePassword ? (
          <div className={style.passwordReset}>
            <Button
              variant="text"
              size="small"
              onClick={() => setChangePassword(true)}
              icon={<span className="material-symbols-sharp">lock_reset</span>}
            >
              {t(`${keyI18n}.resetPassword`)}
            </Button>
          </div>
        ) : (
          <div className={style.passwordFields}>
            {editing && (
              <p className={`pgm-body-small ${style.passwordHint}`}>
                {t(`${keyI18n}.${own ? "resetOwnPasswordHint" : "resetPasswordHint"}`)}
              </p>
            )}
            <div className={style.field}>
              <Input
                variant="outlined"
                type={showPassword ? "text" : "password"}
                label={t(`${keyI18n}.${editing ? "newPassword" : "password"}`)}
                value={password}
                required
                autoComplete="new-password"
                fullWidth
                hideClearButton
                trailingIcon={visibilityButton}
                supportingText={t(`${keyI18n}.passwordHint`, { min: MIN_PASSWORD })}
                error={Boolean(errorText("password"))}
                errorText={errorText("password")}
                onInput={(event: MaterialInputEvent) => setPassword(event.currentTarget.value)}
              />
            </div>
            <div className={style.field}>
              <Input
                variant="outlined"
                type={showPassword ? "text" : "password"}
                label={t(`${keyI18n}.passwordConfirmation`)}
                value={passwordConfirmation}
                required
                autoComplete="new-password"
                fullWidth
                hideClearButton
                error={Boolean(errorText("passwordConfirmation"))}
                errorText={errorText("passwordConfirmation")}
                onInput={(event: MaterialInputEvent) => setPasswordConfirmation(event.currentTarget.value)}
              />
            </div>
            {editing && (
              <div className={style.passwordReset}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setChangePassword(false);
                    setPassword("");
                    setPasswordConfirmation("");
                    setErrors((current) => ({ ...current, password: undefined, passwordConfirmation: undefined }));
                  }}
                >
                  {t(`${keyI18n}.keepPassword`)}
                </Button>
              </div>
            )}
          </div>
        )}

        {(failed || baseError) && (
          <p className={`pgm-body-medium ${style.formError}`} role="alert">
            {t(`${keyI18n}.errors.generic`)}
          </p>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
